import React from 'react';
import { Link } from 'react-router';
import { FiCode, FiBookOpen, FiHome, FiTrendingUp, FiMapPin, FiMail } from 'react-icons/fi';
import { useTheme } from '../context/ThemeContext.jsx';
import Header from '../components/common/Header.jsx';
import Footer from '../components/common/Footer.jsx';

function AboutUsContent() {
  const { colors, spacing, fonts, borderRadius, shadows } = useTheme();

  const pageStyles = {
    minHeight: '100vh',
    backgroundColor: colors.background,
    color: colors.text,
    fontFamily: fonts.families.primary,
    lineHeight: fonts.lineHeights.normal,
    transition: 'all 0.3s ease'
  };

  const mainStyles = {
    maxWidth: '900px',
    margin: `${spacing.scale.xl} auto`,
    padding: `0 ${spacing.scale.lg}`
  };

  const contentContainerStyles = {
    backgroundColor: colors.surface,
    borderRadius: borderRadius.lg,
    boxShadow: shadows.md,
    overflow: 'hidden',
    border: `1px solid ${colors.border}`
  };

  const contentStyles = {
    padding: `${spacing.scale.xl} ${spacing.scale.lg}`
  };

  const sectionStyles = {
    marginBottom: spacing.scale.xl
  };

  const headerSectionStyles = {
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
    color: colors.textInverse,
    padding: `${spacing.scale.lg} ${spacing.scale.md}`,
    textAlign: 'center'
  };

  const titleStyles = {
    fontSize: fonts.sizes['2xl'].desktop,
    marginBottom: 0,
    fontWeight: fonts.weights.bold,
    fontFamily: fonts.families.secondary,
    color: '#ffffff',
    textShadow: `1px 1px 2px rgba(0, 0, 0, 0.5)`
  };

  return (
    <div style={pageStyles}>
      <Header />

      <main style={mainStyles}>
        <div style={contentContainerStyles}>
          <section style={headerSectionStyles}>
            <h1 style={titleStyles}>About LondaLab</h1>
          </section>

          <div style={contentStyles}>
            {/* Introduction */}
            <section style={{ marginBottom: spacing.scale.lg }}>
              <p style={{
                fontSize: fonts.sizes.base.desktop,
                color: colors.text,
                lineHeight: fonts.lineHeights.relaxed,
                marginBottom: spacing.scale.md
              }}>
                Welcome to LondaLab, home of Human Centered Intelligent Apps. LondaLab is the publishing lab for all Victoria Londe apps: the place where our ideas become apps that people actually use. From everyday life to learning to helping a small business get noticed, we design, build, and publish apps that meet people where they are.
              </p>
            </section>

            {/* Vision Box */}
            <div id="vision" style={{
              backgroundColor: `${colors.info}10`,
              border: `1px solid ${colors.info}40`,
              borderRadius: borderRadius.md,
              padding: spacing.scale.lg,
              marginBottom: spacing.scale.xl
            }}>
              <h3 style={{
                color: colors.info,
                marginTop: 0,
                marginBottom: spacing.scale.sm,
                fontSize: fonts.sizes.base.desktop,
                fontWeight: fonts.weights.semibold,
                fontFamily: fonts.families.secondary
              }}>
                Our Vision
              </h3>
              <p style={{
                fontSize: fonts.sizes.sm.desktop,
                color: colors.text,
                lineHeight: fonts.lineHeights.relaxed,
                marginBottom: 0
              }}>
                At LondaLab, we believe technology should serve people first. Every app we publish starts with a human need (a habit to build, a skill to learn, a livelihood to grow) and is designed around the person using it, not the technology behind it.
              </p>
            </div>

            {/* What We Do */}
            <section style={sectionStyles}>
              <h2 style={{
                color: colors.text,
                fontSize: fonts.sizes.lg.desktop,
                marginBottom: spacing.scale.md,
                fontFamily: fonts.families.secondary,
                fontWeight: fonts.weights.semibold
              }}>
                What We Do
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.scale.md }}>
                <div id="everyday-life">
                  <h3 style={{
                    color: colors.text,
                    fontSize: fonts.sizes.base.desktop,
                    fontWeight: fonts.weights.semibold,
                    marginBottom: spacing.scale.xs,
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.scale.sm
                  }}>
                    <FiHome size="1.2em" color={colors.secondary} aria-hidden="true" /> Everyday Life
                  </h3>
                  <p style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    lineHeight: fonts.lineHeights.normal,
                    marginTop: 0,
                    marginBottom: 0
                  }}>
                    Apps for the things people actually do at home and in their daily routines. Practical, friendly, and built to fit into real life.
                  </p>
                </div>

                <div id="learning-growth">
                  <h3 style={{
                    color: colors.text,
                    fontSize: fonts.sizes.base.desktop,
                    fontWeight: fonts.weights.semibold,
                    marginBottom: spacing.scale.xs,
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.scale.sm
                  }}>
                    <FiBookOpen size="1.2em" color={colors.secondary} aria-hidden="true" /> Learning & Growth
                  </h3>
                  <p style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    lineHeight: fonts.lineHeights.normal,
                    marginTop: 0,
                    marginBottom: 0
                  }}>
                    Apps that help people build skills and confidence, designed with dignity and encouragement at the center.
                  </p>
                </div>

                <div id="small-business">
                  <h3 style={{
                    color: colors.text,
                    fontSize: fonts.sizes.base.desktop,
                    fontWeight: fonts.weights.semibold,
                    marginBottom: spacing.scale.xs,
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.scale.sm
                  }}>
                    <FiTrendingUp size="1.2em" color={colors.secondary} aria-hidden="true" /> Small Business & Livelihoods
                  </h3>
                  <p style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    lineHeight: fonts.lineHeights.normal,
                    marginTop: 0,
                    marginBottom: 0
                  }}>
                    Intelligent, agentic apps for small businesses and sole proprietors, including tools that give owners real marketing exposure without an agency budget.
                  </p>
                </div>

                <div id="the-lab">
                  <h3 style={{
                    color: colors.text,
                    fontSize: fonts.sizes.base.desktop,
                    fontWeight: fonts.weights.semibold,
                    marginBottom: spacing.scale.xs,
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.scale.sm
                  }}>
                    <FiCode size="1.2em" color={colors.secondary} aria-hidden="true" /> The Lab
                  </h3>
                  <p style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    lineHeight: fonts.lineHeights.normal,
                    marginTop: 0,
                    marginBottom: 0
                  }}>
                    We are always building. Ideas get prototyped, tested with real people, and published to the app stores when they're ready.
                  </p>
                </div>
              </div>
            </section>

            {/* Our Values */}
            <section id="values" style={sectionStyles}>
              <h2 style={{
                color: colors.text,
                fontSize: fonts.sizes.lg.desktop,
                marginBottom: spacing.scale.md,
                fontFamily: fonts.families.secondary,
                fontWeight: fonts.weights.semibold
              }}>
                Our Values
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.scale.sm }}>
                <div>
                  <strong style={{
                    color: colors.secondary,
                    fontSize: fonts.sizes.base.desktop,
                    fontWeight: fonts.weights.semibold
                  }}>
                    Innovation:
                  </strong>
                  <span style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    marginLeft: spacing.scale.xs
                  }}>
                    We embrace new technologies and creative approaches to solve complex problems.
                  </span>
                </div>
                <div>
                  <strong style={{
                    color: colors.secondary,
                    fontSize: fonts.sizes.base.desktop,
                    fontWeight: fonts.weights.semibold
                  }}>
                    Quality:
                  </strong>
                  <span style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    marginLeft: spacing.scale.xs
                  }}>
                    Excellence isn't just a goal, it's our standard. We take pride in delivering products that exceed expectations.
                  </span>
                </div>
                <div>
                  <strong style={{
                    color: colors.secondary,
                    fontSize: fonts.sizes.base.desktop,
                    fontWeight: fonts.weights.semibold
                  }}>
                    Community:
                  </strong>
                  <span style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    marginLeft: spacing.scale.xs
                  }}>
                    We build apps that support real people and the communities they live and work in.
                  </span>
                </div>
                <div>
                  <strong style={{
                    color: colors.secondary,
                    fontSize: fonts.sizes.base.desktop,
                    fontWeight: fonts.weights.semibold
                  }}>
                    Integrity:
                  </strong>
                  <span style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    marginLeft: spacing.scale.xs
                  }}>
                    We operate with transparency, ethical practices, and respect for our users and partners.
                  </span>
                </div>
              </div>
            </section>

            {/* Contact Information */}
            <section id="contact" style={sectionStyles}>
              <h2 style={{
                color: colors.text,
                fontSize: fonts.sizes.lg.desktop,
                marginBottom: spacing.scale.md,
                fontFamily: fonts.families.secondary,
                fontWeight: fonts.weights.semibold
              }}>
                Contact Information
              </h2>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: spacing.scale.md
              }}>
                <div style={{
                  backgroundColor: colors.backgroundSecondary,
                  borderRadius: borderRadius.md,
                  padding: spacing.scale.md,
                  border: `1px solid ${colors.border}`,
                  textAlign: 'center'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: spacing.scale.sm,
                    marginBottom: spacing.scale.xs
                  }}>
                    <FiMapPin size="1.1em" color={colors.secondary} aria-hidden="true" />
                    <h3 style={{
                      fontSize: fonts.sizes.base.desktop,
                      fontWeight: fonts.weights.semibold,
                      color: colors.text,
                      margin: 0,
                      fontFamily: fonts.families.secondary
                    }}>
                      Business Address
                    </h3>
                  </div>
                  <div style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    lineHeight: fonts.lineHeights.relaxed
                  }}>
                    <strong>LondaLab</strong><br />
                    2883 Hopyard Rd<br />
                    Pleasanton, CA 94588
                  </div>
                </div>

                <div style={{
                  backgroundColor: colors.backgroundSecondary,
                  borderRadius: borderRadius.md,
                  padding: spacing.scale.md,
                  border: `1px solid ${colors.border}`,
                  textAlign: 'center'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: spacing.scale.sm,
                    marginBottom: spacing.scale.xs
                  }}>
                    <FiMail size="1.1em" color={colors.secondary} aria-hidden="true" />
                    <h3 style={{
                      fontSize: fonts.sizes.base.desktop,
                      fontWeight: fonts.weights.semibold,
                      color: colors.text,
                      margin: 0,
                      fontFamily: fonts.families.secondary
                    }}>
                      Email
                    </h3>
                  </div>
                  <div style={{
                    fontSize: fonts.sizes.sm.desktop,
                    color: colors.textSecondary,
                    lineHeight: fonts.lineHeights.relaxed
                  }}>
                    <a
                      href="mailto:hello@londalab.com"
                      style={{
                        color: colors.secondary,
                        textDecoration: 'none',
                        fontWeight: fonts.weights.medium
                      }}
                      onMouseEnter={(e) => e.target.style.color = colors.secondaryHover}
                      onMouseLeave={(e) => e.target.style.color = colors.secondary}
                    >
                      hello@londalab.com
                    </a>
                  </div>
                </div>
              </div>
            </section>

            {/* Victoria Londe */}
            <section id="victoria-londe" style={{ marginBottom: 0 }}>
              <p style={{
                fontSize: fonts.sizes.sm.desktop,
                color: colors.textSecondary,
                lineHeight: fonts.lineHeights.relaxed,
                textAlign: 'center',
                marginBottom: 0
              }}>
                LondaLab is the app publishing lab of{' '}
                <a
                  href="https://victorialonde.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: colors.secondary,
                    textDecoration: 'none',
                    fontWeight: fonts.weights.medium
                  }}
                  onMouseEnter={(e) => e.target.style.color = colors.secondaryHover}
                  onMouseLeave={(e) => e.target.style.color = colors.secondary}
                >
                  Victoria Londe
                </a>
                . For stories, writing, and everything else, visit VictoriaLonde.com.
              </p>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default function AboutPage() {
  return <AboutUsContent />;
}
